/**
 * Pure logic for the referral prompt snooze field: an amount plus a unit (minutes, hours, days).
 *
 * The server stores ONE figure, `referralPromptSnoozeMinutes`, in whole minutes. The unit is a
 * display choice only and is never saved; on load it is re-derived as the largest unit the stored
 * length divides into evenly.
 *
 * No copy lives here: unit labels, errors and the length reading are translation keys under
 * `admin.referralSnooze`, resolved by the field.
 */

export const SNOOZE_UNITS = [
  // `max` caps what may be typed in that unit, so a longer length is entered in the next unit up.
  // Labels are `admin.referralSnooze.units.<key>`.
  { key: 'minutes', minutes: 1, max: 60 },
  { key: 'hours', minutes: 60, max: 24 },
  { key: 'days', minutes: 24 * 60 }
];

const unitOf = (key) => SNOOZE_UNITS.find((u) => u.key === key) || SNOOZE_UNITS[1];

const fitsUnit = (amount, unit) => unit.max === undefined || amount <= unit.max;

/**
 * Which unit a whole length opens in, first match wins. Hours leads so that anything up to its
 * 24-hour cap reads in hours (the 1440-minute default opens as 24 hours, not 1 day); longer whole
 * days fall to Days, and Minutes takes what neither divides.
 */
const DISPLAY_PREFERENCE = ['hours', 'days', 'minutes'];

/**
 * The stored minutes as the amount + unit an admin would have typed, e.g. 1440 → 24 hours, 2880 →
 * 2 days. Takes the first unit in `DISPLAY_PREFERENCE` that divides evenly and is within its cap;
 * otherwise a decimal in the smallest unit that fits (90 → 1.5 hours, 1500 → 1.0416… days).
 */
export const snoozeFromMinutes = (minutes) => {
  const total = Math.round(Number(minutes));
  if (!Number.isFinite(total) || total <= 0) return { amount: '', unit: 'hours' };

  const unit =
    DISPLAY_PREFERENCE.map(unitOf).find(
      (u) => total % u.minutes === 0 && fitsUnit(total / u.minutes, u)
    ) || SNOOZE_UNITS.find((u) => fitsUnit(total / u.minutes, u));
  return { amount: String(total / unit.minutes), unit: unit.key };
};

/**
 * Why an amount + unit is not a usable snooze, as the key of the message to show under the field
 * (`admin.referralSnooze.errors.<key>`), or null when it is valid.
 */
export const snoozeError = (amount, unitKey) => {
  const unit = unitOf(unitKey);
  if (amount !== '' && amount !== null && amount !== undefined && Number(amount) > unit.max) {
    return unit.key === 'minutes' ? 'maxMinutes' : 'maxHours';
  }
  return snoozeMinutes(amount, unitKey) === null ? 'invalid' : null;
};

/**
 * Whole minutes for an amount + unit — what gets saved — or null when the entry is not a usable
 * length: blank, not a number, over the unit's cap, under a minute, or landing between two minutes.
 */
export const snoozeMinutes = (amount, unitKey) => {
  if (amount === '' || amount === null || amount === undefined) return null;
  const unit = unitOf(unitKey);
  if (!fitsUnit(Number(amount), unit)) return null;
  const raw = Number(amount) * unit.minutes;
  if (!Number.isFinite(raw)) return null;

  const minutes = Math.round(raw);
  if (minutes < 1 || Math.abs(raw - minutes) > 1e-6) return null;
  return minutes;
};

/**
 * A length read out in the admin's language for the field's hint, starting from the unit they
 * picked so it echoes what they typed: 1440 in Hours → "24 hours", in Days → "1 day"; 90 in Hours
 * → "1 hour 30 minutes". `t` is i18next's; each part is
 * `admin.referralSnooze.duration.<day|hour|minute>` with `count`.
 */
export const describeSnooze = (totalMinutes, t, unitKey = 'days') => {
  const days = unitKey === 'days' ? Math.floor(totalMinutes / 1440) : 0;
  const hours = unitKey === 'minutes' ? 0 : Math.floor((totalMinutes - days * 1440) / 60);
  const minutes = totalMinutes - days * 1440 - hours * 60;
  const part = (count, unit) =>
    count ? t(`admin.referralSnooze.duration.${unit}`, { count }) : null;
  return [part(days, 'day'), part(hours, 'hour'), part(minutes, 'minute')]
    .filter(Boolean)
    .join(' ');
};
